/**
 * useViewport Hook
 * 
 * Unified viewport size detection with a single source of truth.
 * Replaces scattered isMobile/isDesktop checks throughout the codebase.
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.mobileBreakpoint - Mobile breakpoint in px (default: 768)
 * @param {number} options.desktopBreakpoint - Desktop breakpoint in px (default: 1024)
 * 
 * @returns {Object} Viewport state and helpers
 * @returns {string} viewportSize - 'mobile' | 'tablet' | 'desktop'
 * @returns {boolean} isMobile - Whether current screen is mobile
 * @returns {boolean} isTablet - Whether current screen is tablet
 * @returns {boolean} isDesktop - Whether current screen is desktop
 * @returns {boolean} isSmallScreen - Whether current screen is mobile or tablet
 */

import { useState, useEffect, useMemo } from 'react';

const DEFAULT_MOBILE_BREAKPOINT = 768;
const DEFAULT_DESKTOP_BREAKPOINT = 1024;
const RESIZE_DEBOUNCE_MS = 150;

/**
 * Debounce function
 */
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Get viewport size from window width
 */
const getViewportSize = (width, mobileBreakpoint, desktopBreakpoint) => {
  if (width < mobileBreakpoint) {
    return 'mobile';
  }
  if (width < desktopBreakpoint) {
    return 'tablet';
  }
  return 'desktop';
};

export const useViewport = (options = {}) => {
  const {
    mobileBreakpoint = DEFAULT_MOBILE_BREAKPOINT,
    desktopBreakpoint = DEFAULT_DESKTOP_BREAKPOINT,
  } = options;

  const [viewportSize, setViewportSize] = useState(() => {
    if (typeof window === 'undefined') {
      return 'desktop';
    }
    return getViewportSize(window.innerWidth, mobileBreakpoint, desktopBreakpoint);
  });

  /**
   * Handle window resize with debouncing
   */
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = debounce(() => {
      try {
        const width = window.innerWidth;
        const newSize = getViewportSize(width, mobileBreakpoint, desktopBreakpoint);
        setViewportSize(newSize);
      } catch {
        // Ignore errors when window is not available (e.g., during test cleanup)
      }
    }, RESIZE_DEBOUNCE_MS);

    // Initial check
    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [mobileBreakpoint, desktopBreakpoint]);

  // Computed helpers for backward compatibility and convenience
  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(() => ({
    viewportSize,
    isMobile: viewportSize === 'mobile',
    isTablet: viewportSize === 'tablet',
    isDesktop: viewportSize === 'desktop',
    isSmallScreen: viewportSize === 'mobile' || viewportSize === 'tablet',
  }), [viewportSize]);
};




